import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
  video,
} from '../helpers';

const distPerm = permissionTable([
  ['manage_distribution', 'Required to open Distribution / channel manager screens'],
]);
const pricePerm = permissionTable([
  ['manage_dynamic_pricing', 'Required to open Dynamic Pricing tabs and publish rates'],
]);

export const distributionGuides = [
  buildGuide({
    id: 'distribution-overview',
    title: 'Distribution Overview',
    moduleId: 'distribution',
    summary:
      'Connect OTAs, map rooms/rates, push inventory, block sales, and configure NexaStay direct booking.',
    keywords: [
      'distribution',
      'channel manager',
      'ota',
      'mapping',
      'allotment',
      'nexastay',
    ],
    pathHint: 'Hotel → Distribution',
    related: [
      'channel-setup',
      'channel-mapping',
      'channel-inventory',
      'pms-inventory',
      'inventory-blocks',
      'nexastay-setup',
    ],
    videos: [
      video(
        'a2n9HDRtKrg',
        'Manage inventory and rates across OTAs',
        'See how Roomnexa simplifies multi-OTA inventory and rate control.'
      ),
    ],
    overview: [
      ...paragraphs(
        'Distribution is Roomnexa’s channel manager workspace. Revenue, front-office supervisors, and property managers use it to connect online travel agencies (OTAs), map PMS room types and rate plans to channel room/rate codes, control how many rooms each channel may sell, and stop sales for renovations or private events.',
        'The module also hosts NexaStay — Roomnexa’s direct booking engine — so you can sell on your own site without OTA commission while still honouring the same PMS inventory and rates.',
        'ChannelHub (backend sync) pushes availability and rates outward and ingests reservation events inward. When mapping or allotment is wrong, guests may book the wrong category or you may overbook; when sync fails, OTA bookings may not appear in the PMS on time.',
        'Treat Distribution as a controlled go-live surface: stabilize Configuration masters first (room types, rooms, rate plans, Manage Rate), then credentials → mapping → allotment → test booking → monitor. Never activate a live channel before mapping is verified.'
      ),
      table(
        ['Section', 'What staff do here'],
        [
          ['Channel Setup', 'Add channel credentials, type, update mode, active flag'],
          ['Channel Inventory', 'Set allocated rooms (allotment) per channel + room type'],
          ['Channel Mapping', 'Map PMS room type / rate plan ↔ channel room & rate codes'],
          ['Inventory (PMS)', 'Review PMS-side availability while managing channels'],
          ['Inventory Block', 'Stop sales for dates/types on PMS and/or channels'],
          ['NexaStay', 'Theme, public URL slug, payment modes, direct booking preview'],
        ]
      ),
    ],
    beforeYouBegin: [
      distPerm,
      bullets([
        'Room types, physical rooms, and rate plans exist under Configuration',
        'Daily rates exist in Manage Rate for the sell window you will open online',
        'Extranet hotel ID / API codes ready from each OTA or channel partner',
        'Agree who owns Distribution edits (usually revenue or GM — not every FO agent)',
      ]),
      callout(
        'important',
        'Stabilize masters first',
        'Finish room types and rates before mapping OTAs. Renaming a room type after go-live usually forces remapping and a full inventory re-push.'
      ),
    ],
    steps: [
      heading('Recommended go-live order'),
      steps([
        {
          text: 'Open Hotel → Distribution. Confirm you are on the correct property in the top bar.',
        },
        {
          text: 'Channel Setup — add each channel with Hotel ID / credentials, Channel Type (OTA, Channel Manager, Direct, PMS), Update Mode, and leave Active off until mapping is ready.',
        },
        {
          text: 'Channel Mapping — for each channel, map every sellable PMS room type to Channel Room Code and (when required) Rate Plan to Channel Rate Code. Mark mappings Active.',
        },
        {
          text: 'Channel Inventory — set Allocated Rooms per channel and room type so the sum of aggressive allotments cannot exceed physical sellable stock without a deliberate overbook policy.',
        },
        {
          text: 'Review Inventory (PMS) for the same dates so PMS availability and channel allotments tell the same story.',
        },
        {
          text: 'Create an Inventory Block for any renovation/closed dates and select PMS + relevant channels.',
        },
        {
          text: 'Configure NexaStay (slug, theme, payment options) and place a test direct booking.',
        },
        {
          text: 'Activate the channel in Channel Setup, place a test OTA booking on a low-risk date, and confirm it arrives as a PMS booking.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Section cards / NotchTabs — Channel Setup, Channel Inventory, Channel Mapping, Inventory (PMS), Inventory Block, NexaStay',
          'Progress / count badges on section cards (when data is loaded)',
          'Shortcut to Dynamic Pricing from Distribution context when package allows',
        ],
        'Distribution hub'
      ),
      fieldTable([
        ['Channel Setup', '—', 'Credentials, type, update mode, rate limit, Active'],
        ['Channel Mapping', '—', 'PMS room/rate ↔ channel room/rate codes'],
        ['Channel Inventory', '—', 'Allocated rooms (allotment) per channel type'],
        ['Inventory Block', '—', 'Date range, room type, num rooms, channels, reason'],
        ['NexaStay', '—', 'Public booking site branding, URL, payments'],
      ]),
    ],
    workflow: [
      workflow('Go live on a new OTA', [
        'Collect extranet hotel ID / codes',
        'Channel Setup (Inactive)',
        'Create OTA rooms/rates on extranet',
        'Channel Mapping (1:1 preferred)',
        'Set Channel Inventory allotment',
        'Test booking → PMS',
        'Activate channel',
        'Monitor first 48 hours',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'ChannelHub translates between PMS masters and channel codes using Channel Mapping. Unmapped or inactive maps do not distribute correctly — availability may stay closed or land on the wrong OTA room.',
        'Channel Inventory allotments constrain how many rooms a channel may sell for a room type. PMS bookings and other channels still consume shared physical inventory; over-allocating across channels is a common path to overbook.',
        'Inventory Blocks reduce sellable stock for a date range on selected channels and/or PMS. Existing reservations on those dates are not automatically cancelled — relocate guests before blocking occupied inventory.',
        'Inbound OTA reservations become Roomnexa bookings when sync/webhooks succeed. Front office processes them like any other arrival; distribution issues are usually mapping, allotment, or sync — not “missing Reservation form”.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'One change at a time',
        'When debugging overbook or wrong-room sales, change either mapping or allotment first — not both — then re-check the extranet.'
      ),
      callout(
        'warning',
        'Extranet vs PMS',
        'Manual long-term selling only on the OTA extranet fights ChannelHub. Prefer Roomnexa allotment/blocks, then verify extranet.'
      ),
      callout(
        'note',
        'Direct vs OTA',
        'NexaStay and OTAs share the same PMS inventory truth. A block or sold-out type should protect both when configured correctly.'
      ),
    ],
    bestPractices: [
      bullets([
        'Keep a written channel go-live checklist (credentials → map → allot → test → activate).',
        'Screenshot Channel Mapping after go-live and store it with revenue SOPs.',
        'Monitor the first 48 hours of a new OTA closely (arrivals list, allotment, rate parity).',
        'Assign one owner for Distribution edits; FO should escalate, not freestyle remap.',
        'Align OTA room names with PMS room type names where possible (e.g. Deluxe Double ↔ Deluxe Double).',
        'Coordinate Inventory Blocks with Housekeeping maintenance / OOO room status.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Activating a channel before mapping is complete.',
        'Mapping two PMS room types to one OTA room (or one PMS type to two OTA rooms) without a pooling plan.',
        'Sum of channel allotments far above physical rooms.',
        'Blocking inventory only in PMS while OTAs stay open.',
        'Renaming room types after OTA go-live without remapping.',
        'Ignoring a test booking that never appeared in Booking List.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'OTA sold a closed / renovation room',
            'Missing block on channel or allotment still open',
            'Check Inventory Block channels + Channel Inventory; re-sync; verify extranet',
          ],
          [
            'Booking not in PMS',
            'ChannelHub sync/event failure or wrong hotel code',
            'Confirm Channel Setup Hotel ID; note OTA booking id; contact support with timestamps',
          ],
          [
            'Wrong room type on OTA booking',
            'Incorrect Channel Mapping',
            'Fix Channel Room Code map; push inventory; spot-check next booking',
          ],
          [
            'Extranet availability stale',
            'Update mode / sync delay / inactive channel',
            'Confirm Active; retry inventory push; check last sync on channel card',
          ],
          [
            'Module missing',
            'Package or manage_distribution permission',
            'Ask admin to enable Distribution for role/package',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'What is ChannelHub?',
          a: 'The backend sync engine that handles inventory/rate pushes and reservation webhooks/events. Staff primarily use the Distribution UI; deep sync failures may need support with the OTA booking id.',
        },
        {
          q: 'Do I need Dynamic Pricing to sell on OTAs?',
          a: 'No. Distribution works with Manage Rate baselines. Dynamic Pricing is optional for recommendation/publish workflows when your package includes it.',
        },
        {
          q: 'Which OTAs are supported?',
          a: 'Configure the channels shown in Channel Setup for your hotel package/integrations. If a channel is missing, ask your Roomnexa admin or account contact.',
        },
        {
          q: 'Is NexaStay an OTA?',
          a: 'No. NexaStay is Roomnexa’s direct booking website/engine. It lives under Distribution → NexaStay and sells without OTA commission when guests book your link.',
        },
        {
          q: 'Who should edit Distribution daily?',
          a: 'Usually revenue managers or property managers. Front office uses Dashboard/Booking for arrivals; escalate allotment and mapping issues rather than editing live maps casually.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'channel-setup',
    title: 'Channel Setup',
    moduleId: 'distribution',
    summary: 'Add and configure OTA/channel credentials, update mode, rate limits, and activation.',
    keywords: [
      'channel setup',
      'ota credentials',
      'hotel id',
      'booking.com',
      'expedia',
      'update mode',
    ],
    pathHint: 'Distribution → Channel Setup',
    related: ['channel-mapping', 'distribution-overview', 'channel-inventory'],
    overview: [
      ...paragraphs(
        'Channel Setup is where you register each distribution partner Roomnexa will talk to — OTAs, a channel manager, or other connection types supported for your property.',
        'Each channel card stores identity (name, logo), the Hotel ID / property code the partner expects, Channel Type, Update Mode (how inventory/rate updates are sent), optional rate limit, and whether the channel is Active.',
        'Credentials and hotel codes are sensitive. A typo in Hotel ID can link the wrong property or fail auth entirely. Keep Active off until Channel Mapping and allotments are ready for a controlled go-live.',
        'After save, channel cards typically show update mode, rate limit, last sync signals, and Active/Inactive status so revenue staff can audit connectivity at a glance.'
      ),
    ],
    beforeYouBegin: [
      distPerm,
      bullets([
        'Extranet / partner portal access for the property',
        'Hotel ID / property code copied carefully (no spaces/typos)',
        'API keys or partner credentials if your integration requires them',
        'Agreement on Instant vs Scheduled vs Manual update mode with your integrator',
      ]),
    ],
    steps: [
      heading('Add a channel'),
      steps([
        {
          text: 'Open Distribution → Channel Setup.',
        },
        {
          text: 'Start Add / create channel. Enter Channel Name (e.g. “Booking.com – City Property”).',
        },
        {
          text: 'Enter Hotel ID exactly as shown in the channel extranet or partner sheet.',
        },
        {
          text: 'Optional: upload Channel Logo for easier recognition on cards.',
        },
        {
          text: 'Select Channel Type: OTA, PMS, Direct Booking, or Channel Manager — match how the connection actually works.',
        },
        {
          text: 'Select Update Mode: Instant, Scheduled, or Manual — per your integration design.',
        },
        {
          text: 'Set Rate Limit Per Minute if provided (protects partner APIs from burst traffic).',
        },
        {
          text: 'Leave Active off until mapping and allotment are done. Save.',
        },
        {
          text: 'When ready to sell, toggle Active on and verify last sync / a test booking.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Channel Name', 'Yes', 'Staff-facing label on cards and selectors'],
        ['Hotel ID', 'Yes', 'Partner property code — must match extranet'],
        ['Channel Logo', 'No', 'Visual identity on Distribution cards'],
        ['Channel Type', 'Yes', 'OTA / PMS / Direct Booking / Channel Manager'],
        ['Update Mode', 'Yes', 'Instant / Scheduled / Manual push behaviour'],
        ['Rate Limit Per Minute', 'No', 'Throttle for sync calls'],
        ['Active', 'Yes', 'Whether the channel is live for sync/sales'],
      ]),
      bullets(
        [
          'Channel list / cards with type chip, update mode, rate limit, last sync, Active badge',
          'Edit / save controls on the setup form',
        ],
        'What you see'
      ),
    ],
    workflow: [
      workflow('Add channel safely', [
        'Collect codes from extranet',
        'Enter Channel Setup (Inactive)',
        'Save & verify Hotel ID',
        'Complete Mapping + Inventory',
        'Activate',
        'Test booking',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Inactive channels should not receive live pushes the same way Active channels do. Prefer drafting credentials inactive, completing mapping, then activating.',
        'Hotel ID is the join key between Roomnexa and the partner property. Wrong IDs cause auth failures or, worse, sync against the wrong hotel.',
        'Update Mode and rate limits affect how aggressively Roomnexa talks to the partner. Changing them mid-peak can delay availability updates — coordinate with whoever owns the integration.',
        'Channel Setup alone does not sell rooms. Mapping and allotment (and rates) are still required before guests can book correctly.'
      ),
    ],
    tips: [
      callout(
        'warning',
        'Treat credentials like passwords',
        'Limit who has manage_distribution. Do not paste API keys into shared chat logs.'
      ),
      callout(
        'tip',
        'Clear naming',
        'If you have multiple properties, include the city or brand in Channel Name so staff never edit the wrong connection.'
      ),
      callout(
        'note',
        'Logo optional',
        'Logos help ops recognition but do not affect sync. Prioritize correct Hotel ID over branding.'
      ),
    ],
    bestPractices: [
      bullets([
        'Document which staff member owns each extranet login.',
        'Keep a secure vault entry for Hotel IDs and keys — not a spreadsheet on a shared desktop.',
        'Activate only after a successful mapped test on a quiet date.',
        'Re-verify Hotel ID after any partner “property merge” or ownership change.',
        'Review Active channels monthly; deactivate unused test connections.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Activating before Channel Mapping exists.',
        'Typos or trailing spaces in Hotel ID.',
        'Selecting Channel Type that does not match the real integration.',
        'Sharing one channel record across two physical hotels.',
        'Leaving test/sandbox credentials on a live property.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Auth / connection failed',
            'Wrong credentials or Hotel ID',
            'Re-copy from extranet; save; retry; contact partner support if still failing',
          ],
          [
            'Wrong property linked',
            'Hotel ID belongs to another hotel',
            'Verify code with channel account manager; correct and re-test',
          ],
          [
            'No sync activity',
            'Channel Inactive or update mode Manual with no push',
            'Check Active; confirm Update Mode; trigger inventory push after mapping',
          ],
          [
            'Cannot save',
            'Required fields empty or validation error',
            'Complete Channel Name, Hotel ID, Type, Update Mode',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Which OTAs can I add?',
          a: 'Those enabled for your hotel package/integrations and shown as available in Setup. Ask your admin if an expected partner is missing.',
        },
        {
          q: 'Should Update Mode be Instant?',
          a: 'Use what your integrator recommends. Instant is common for live OTAs; Scheduled/Manual may suit constrained APIs. Do not change casually in peak season.',
        },
        {
          q: 'Can I edit Hotel ID later?',
          a: 'Yes, but treat it as a breaking change — re-verify mapping and run a test booking afterward.',
        },
        {
          q: 'Does Active mean we are selling?',
          a: 'Active enables the live connection. You still need correct mapping, allotment > 0, open rates, and no blocking inventory for those dates.',
        },
        {
          q: 'What is Rate Limit Per Minute?',
          a: 'A throttle so sync calls stay within partner API limits. Leave at the recommended value unless support asks you to change it.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'channel-mapping',
    title: 'Channel Mapping',
    moduleId: 'distribution',
    summary:
      'Map PMS room types and rate plans to channel room codes and rate codes.',
    keywords: [
      'channel mapping',
      'room mapping',
      'rate mapping',
      'channel room code',
      'channel rate code',
    ],
    pathHint: 'Distribution → Channel Mapping',
    related: ['channel-setup', 'room-types', 'rate-plans', 'channel-inventory'],
    overview: [
      ...paragraphs(
        'Channel Mapping is the translation layer between Roomnexa masters and what the OTA or channel manager understands. Each row links a PMS room type (and often a rate plan) to a Channel Room Code and Channel Rate Code.',
        'Wrong maps cause the classic guest surprise: they booked “Deluxe” online and arrive to a Standard, or inventory decrements the wrong category. Overbook and rate disputes often start here.',
        'Prefer clear one-to-one maps (Deluxe ↔ Deluxe) unless you intentionally pool inventory. Channel rooms must already exist on the extranet before you map codes.',
        'After saving, verify with a low-risk date: allotment on the mapped OTA room, then a test booking that lands on the expected PMS room type.'
      ),
    ],
    beforeYouBegin: [
      distPerm,
      bullets([
        'Room types stable (names/codes frozen for go-live)',
        'Rate plans created for products you sell online (e.g. RO, CP)',
        'Channel rooms and rates created on the extranet with known codes',
        'Channel Setup record exists for the partner',
      ]),
    ],
    steps: [
      heading('Map a channel'),
      steps([
        {
          text: 'Open Distribution → Channel Mapping.',
        },
        {
          text: 'Select the Channel from the channel selector.',
        },
        {
          text: 'Optional: use “Set all room types at once” helpers when bulk-filling consistent patterns — still verify each code.',
        },
        {
          text: 'For each sellable PMS Room Type, enter the Channel Room Code exactly as on the extranet.',
        },
        {
          text: 'Select Rate Plan where the UI requires it, and enter Channel Rate Code for that product (e.g. BAR RO vs CP).',
        },
        {
          text: 'Toggle Active on mappings you want live; leave unused maps inactive rather than deleting history casually.',
        },
        {
          text: 'Save. Cross-check codes against extranet screenshots.',
        },
        {
          text: 'Push/adjust Channel Inventory for a test date and confirm the correct OTA room opens.',
        },
      ]),
      callout(
        'important',
        'Codes are literal',
        'Channel Room Code and Rate Code are partner identifiers — case and spelling matter. Do not invent codes; copy from extranet.'
      ),
    ],
    screens: [
      fieldTable([
        ['Channel', 'Yes', 'Which partner these maps belong to'],
        ['Room Type (PMS)', 'Yes', 'Roomnexa sellable category'],
        ['Rate Plan (PMS)', 'When required', 'Pricing product mapped to channel rate'],
        ['Channel Room Code', 'Yes', 'OTA/channel room identifier'],
        ['Channel Rate Code', 'When required', 'OTA/channel rate identifier'],
        ['Active', 'Yes', 'Whether this map is used in sync'],
        ['Set all room types at once', 'No', 'Bulk helper — verify every row after use'],
      ]),
    ],
    workflow: [
      workflow('Remap after renovation / rename', [
        'Update Room Types in Configuration',
        'Update extranet room definitions if needed',
        'Edit Channel Mapping codes',
        'Recheck Channel Inventory allotments',
        'Test booking on quiet date',
        'Inform FO of new category names',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Sync uses mapping as the dictionary for inventory, rates, and inbound reservations. Unmapped types will not distribute correctly; inactive maps are ignored for live selling.',
        'One PMS room type mapped to multiple channel rooms (or the reverse) changes how inventory is shared. Only do this with an explicit revenue pooling policy.',
        'Rate plan maps matter when the channel sells multiple products (Room Only vs Breakfast). Mapping only rooms but not rates can leave rates closed or mis-priced on the OTA.',
        'Changing maps mid-stay does not rewrite past bookings; it affects future availability and new reservations. Coordinate changes in low-occupancy windows.'
      ),
    ],
    tips: [
      callout(
        'important',
        '1:1 clarity',
        'Prefer one PMS type → one OTA room unless you intentionally pool. Document any exception.'
      ),
      callout(
        'tip',
        'Name alignment',
        'Keep OTA room names close to PMS names (e.g. “Deluxe King”) so FO can reconcile arrivals quickly.'
      ),
      callout(
        'warning',
        'After type rename',
        'If you rename or split room types, remap the same day or OTAs will sell stale products.'
      ),
    ],
    bestPractices: [
      bullets([
        'Screenshot maps after go-live and store with the channel checklist.',
        'Change maps in low-occupancy windows with FO briefed.',
        'Map only room types you actually stock; do not leave ghost maps Active.',
        'Include meal-plan rate codes when you sell CP/MAP online.',
        'Re-audit mapping whenever the extranet room list changes.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Mapping Deluxe to Standard accidentally.',
        'Copying codes from another hotel’s extranet.',
        'Activating maps for room types with zero physical rooms.',
        'Forgetting rate codes when the channel requires them.',
        'Bulk-filling codes without a second-person check.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Allotment appears on wrong OTA room',
            'Wrong Channel Room Code',
            'Fix map; push inventory; verify extranet room',
          ],
          [
            'OTA booking lands on wrong PMS type',
            'Inbound map mismatch',
            'Correct map; escalate existing bookings manually with FO',
          ],
          [
            'Rates closed on OTA',
            'Missing rate map or Manage Rate empty',
            'Add Channel Rate Code map; set Manage Rate; re-push',
          ],
          [
            'Cannot select room type',
            'Room type missing/inactive in Configuration',
            'Create/enable room type first',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I configure credentials before mapping?',
          a: 'Yes — draft Channel Setup first — but do not activate/sell until mapping is verified.',
        },
        {
          q: 'Must every rate plan be mapped?',
          a: 'Map the plans you sell on that channel. Internal-only corporate plans can stay unmapped if they are not distributed.',
        },
        {
          q: 'What if the OTA has more rooms than we do?',
          a: 'Do not map phantom OTA rooms to real PMS types. Close or remove unused OTA rooms on the extranet.',
        },
        {
          q: 'Does mapping set the price?',
          a: 'No. Mapping links products. Prices come from Manage Rate / Dynamic Pricing publish and channel rate pushes.',
        },
        {
          q: 'Can FO edit mapping?',
          a: 'Usually no — restrict manage_distribution. Wrong maps cause expensive guest and overbook issues.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'channel-inventory',
    title: 'Channel Inventory',
    moduleId: 'distribution',
    summary: 'View and adjust inventory allotments (allocated rooms) per channel and room type.',
    keywords: [
      'channel inventory',
      'allotment',
      'allocated rooms',
      'push inventory',
      'stop sell',
    ],
    pathHint: 'Distribution → Channel Inventory',
    related: ['manage-inventory', 'channel-mapping', 'inventory-blocks', 'pms-inventory'],
    overview: [
      ...paragraphs(
        'Channel Inventory controls how many rooms of each type you allot to a distribution channel. The key field is Allocated Rooms — the allotment the channel may sell for that room type.',
        'Revenue teams use this to protect direct/high-value segments (lower OTA allotment in peak), open more rooms on a strong OTA, or zero allotment to stop sell on a channel without deleting the connection.',
        'Allotment is not the same as physical room count, but it cannot exceed total inventory for the type when the UI enforces max rooms. Over-allocating across many channels without a shared-inventory strategy is a primary overbook cause.',
        'After saving, verify on the channel extranet that availability matches what you intended, especially around city events and sold-out weekends.'
      ),
    ],
    beforeYouBegin: [
      distPerm,
      bullets([
        'Channel Setup and Channel Mapping complete for the partner',
        'Know physical sellable count per room type (minus OOO / long blocks)',
        'Revenue policy for OTA vs direct share in peak dates',
      ]),
    ],
    steps: [
      heading('Adjust allotment'),
      steps([
        {
          text: 'Open Distribution → Channel Inventory.',
        },
        {
          text: 'Select Channel, then Room Type you want to allot.',
        },
        {
          text: 'Enter Allocated Rooms (e.g. 5 of 12 Deluxe rooms to Booking.com). Respect the max inventory shown if the form validates against total.',
        },
        {
          text: 'Toggle Active for this channel inventory row when it should participate in selling.',
        },
        {
          text: 'Save / sync. Confirm the channel card shows updated type counts.',
        },
        {
          text: 'Verify on the OTA extranet for a sample date that availability opened/closed as expected.',
        },
      ]),
      callout(
        'tip',
        'Stop sell quickly',
        'Set Allocated Rooms to 0 for that channel/type (and/or use Inventory Block) when you must halt OTA sales immediately.'
      ),
    ],
    screens: [
      fieldTable([
        ['Channel', 'Yes', 'Partner receiving the allotment'],
        ['Room Type', 'Yes', 'PMS category being allotted'],
        ['Allocated Rooms', 'Yes', 'How many rooms this channel may sell'],
        ['Active', 'Yes', 'Whether this allotment row is live'],
      ]),
      bullets(
        [
          'Channel inventory cards summarizing types and active counts',
          'Form drawer/dialog for create/edit allotment',
        ],
        'What you see'
      ),
    ],
    workflow: [
      workflow('Stop OTA sales for a type', [
        'Select channel + room type',
        'Set Allocated Rooms = 0',
        'Save / sync',
        'Confirm extranet closed',
        'Optional: Inventory Block for date range',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Allotments constrain channel sales for the mapped room type. PMS walk-ins and other channels still consume shared physical stock depending on how inventory is shared.',
        'Setting allotment above physical capacity (when allowed) is an explicit overbook risk — only do this with a clear protection policy (and usually Overbooking Protection blocks or FO monitoring).',
        'Active=false on an inventory row stops that allotment from selling without deleting historical configuration.',
        'Manage Inventory (finance/ops daily grid) and Channel Inventory are related but not identical: Channel Inventory is channel allotment-facing; Manage Inventory is the broader PMS daily inventory surface.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Peak city events',
        'Reduce OTA allotment to protect direct or contracted segments if that is your revenue strategy — then watch pickup hourly.'
      ),
      callout(
        'warning',
        'Do not only close extranet',
        'Closing rooms only on the OTA extranet while Roomnexa still pushes allotment creates fights. Change Allocated Rooms / blocks here.'
      ),
      callout(
        'note',
        'After mapping changes',
        'Revisit allotments whenever you remap room types so inventory does not sit on an orphaned code.'
      ),
    ],
    bestPractices: [
      bullets([
        'Reconcile extranet vs PMS availability daily in peak season.',
        'Document target OTA share % per room type for weekends vs weekdays.',
        'Zero allotment before deactivating a channel for a long pause.',
        'Coordinate allotment cuts with sales (groups) and FO (walk-in needs).',
        'After large allotment drops, place a test search on the OTA.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Zeroing PMS invent without closing channel allotments.',
        'Over-allocating the sum of channels beyond physical rooms.',
        'Editing allotment for the wrong channel after multi-OTA go-live.',
        'Leaving allotment open on renovation dates without Inventory Block.',
        'Assuming allotment change is instant on every Update Mode.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Extranet not updated',
            'Sync delay, Inactive channel, or mapping issue',
            'Retry sync; check Channel Setup Active + last sync; verify mapping',
          ],
          [
            'Cannot set allocated above max',
            'UI enforces total inventory cap',
            'Free OOO/blocks or lower other allotments; add rooms only if real',
          ],
          [
            'OTA still selling after 0 allotment',
            'Cache/delay or block not applied to channel',
            'Wait/retry push; add Inventory Block including that channel; check extranet stop-sell',
          ],
          [
            'Wrong type allotment changed',
            'Selected wrong room type',
            'Correct row; re-verify extranet room name',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'How is this different from Manage Inventory?',
          a: 'Manage Inventory is the PMS daily inventory grid. Channel Inventory focuses on per-channel allocated rooms (allotment) for distribution.',
        },
        {
          q: 'Does Allocated Rooms mean those rooms are only for that OTA?',
          a: 'It is the allotment ceiling for that channel/type. Shared inventory rules still apply — FO and other channels can consume the same physical rooms.',
        },
        {
          q: 'Should allotments equal physical rooms on every OTA?',
          a: 'Usually no. Full allotment on every channel multiplies overbook risk. Set deliberate shares.',
        },
        {
          q: 'Can I allot by date?',
          a: 'Use Channel Inventory for channel/type allotment and Inventory Block / Manage Inventory tools for date-specific stops and adjustments per your ops SOP.',
        },
        {
          q: 'What if mapping is wrong but allotment looks fine?',
          a: 'Allotment will open the wrong OTA room. Fix Channel Mapping first, then re-check allotment on the correct extranet room.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pms-inventory',
    title: 'PMS Inventory (Distribution)',
    moduleId: 'distribution',
    summary:
      'PMS-side inventory view inside the Distribution workspace for availability context.',
    keywords: ['pms inventory', 'distribution inventory', 'available rooms', 'total rooms'],
    pathHint: 'Distribution → Inventory (PMS)',
    related: ['manage-inventory', 'channel-inventory', 'inventory-blocks'],
    overview: [
      ...paragraphs(
        'PMS Inventory inside Distribution shows Roomnexa-side availability context while you manage channels — total rooms and available rooms style summaries — so you do not allot blindly against an empty house.',
        'Use it as a sanity check before pushing large channel allotments or releasing Inventory Blocks. It complements Channel Inventory rather than replacing Manage Inventory’s full daily grid.',
        'When PMS availability and OTA extranet disagree, start here, then verify mapping, allotment, blocks, and sync — in that order.',
        'Front-office day-to-day availability still lives on Dashboard / booking flows; this screen is primarily for revenue and distribution operators.'
      ),
    ],
    beforeYouBegin: [
      distPerm,
      bullets([
        'Rooms and room types configured',
        'Understand any active Inventory Blocks and OOO rooms',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Distribution → Inventory (PMS Inventory section).',
        },
        {
          text: 'Review total / available room summaries and room-type context shown on the cards.',
        },
        {
          text: 'If availability is wrong, correct masters via Configuration Rooms, Housekeeping OOO/maintenance, Manage Inventory, or Inventory Block — as appropriate.',
        },
        {
          text: 'Return to Channel Inventory to align allotments with true PMS sellable stock.',
        },
        {
          text: 'After changes, re-check Channel Mapping is still valid for types you sell.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Total rooms', 'Display', 'Physical / configured room capacity context'],
        ['Available rooms', 'Display', 'Sellable availability summary on the Distribution view'],
        ['Room type inventory cards/views', 'Display', 'Per-type context used while managing channels'],
      ]),
      bullets(
        [
          'Distribution section titled Inventory (PMS)',
          'Cards/summaries for PMS inventory — not the full Manage Inventory calendar editor',
        ],
        'What you see'
      ),
    ],
    workflow: [
      workflow('Align PMS & OTA', [
        'Check PMS Inventory',
        'Adjust rooms / OOO / blocks / Manage Inventory',
        'Set Channel Inventory allotment',
        'Verify extranet',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'PMS availability is the source of truth channels should honour after sync. Channel allotments are constraints on top of that truth — not a parallel universe of rooms.',
        'OOO rooms, Inventory Blocks, and bookings reduce what is truly sellable. If PMS Inventory looks low, do not “fix” it by raising OTA allotment.',
        'This Distribution view is for channel operators’ context; deep date-by-date edits typically happen in Manage Inventory and Inventory Block.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Single source of truth',
        'Treat PMS as source of truth; channels follow. Long-term manual extranet selling creates permanent mismatches.'
      ),
      callout(
        'tip',
        'Before festival open',
        'Check PMS Inventory and blocks the morning you raise OTA allotments for a city event.'
      ),
      callout(
        'warning',
        'Mismatch same day',
        'Investigate PMS vs OTA mismatches the same day in peak — overnight delays multiply overbooks.'
      ),
    ],
    bestPractices: [
      bullets([
        'Investigate mismatches same day during peak.',
        'Pair PMS Inventory checks with Channel Inventory before large allotment opens.',
        'Keep OOO and renovation blocks accurate so this view stays trustworthy.',
        'Train FO that Dashboard availability is their daily tool; send distribution issues to revenue.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Editing only on the extranet long-term.',
        'Raising channel allotment when PMS available rooms are already zero.',
        'Ignoring OOO rooms that still appear sellable online.',
        'Assuming this screen replaces Manage Inventory for all edits.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'PMS vs OTA mismatch',
            'Mapping, allotment, block, or sync issue',
            'Remap if needed; adjust allotment/blocks; full inventory push; verify extranet',
          ],
          [
            'Available shows 0 unexpectedly',
            'Blocks, OOO, or full bookings',
            'Check Inventory Block, room status, Booking List for dates',
          ],
          [
            'Totals look wrong after adding rooms',
            'New rooms not saved/active',
            'Verify Configuration → Rooms for type/floor/status',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can FO use this daily?',
          a: 'Managers/revenue staff primarily. FO typically uses Dashboard availability and Booking screens.',
        },
        {
          q: 'Can I edit allotments here?',
          a: 'Use Channel Inventory for channel allotments and Inventory Block / Manage Inventory for stops and daily grid edits.',
        },
        {
          q: 'Why show PMS inventory inside Distribution?',
          a: 'So channel operators can see PMS context without leaving the Distribution workspace.',
        },
        {
          q: 'Does NexaStay read this too?',
          a: 'NexaStay sells against PMS availability/rates. Keeping PMS inventory accurate protects direct and OTA alike.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'inventory-blocks',
    title: 'Inventory Blocks',
    moduleId: 'distribution',
    summary:
      'Block rooms/dates from sale for maintenance, events, overbooking protection, or other closures.',
    keywords: [
      'inventory block',
      'stop sell',
      'renovation block',
      'maintenance',
      'overbooking protection',
    ],
    pathHint: 'Distribution → Inventory Block',
    related: ['housekeeping-maintenance', 'manage-inventory', 'channel-inventory'],
    overview: [
      ...paragraphs(
        'Inventory Blocks remove sellable availability for a room type across a date range — renovations, private events, seasonal closure, enquiry holds, or overbooking protection.',
        'When creating a block you choose Room Type, block type (Maintenance, Event, Overbooking Protection, Manual, Enquiry, Other), From/To dates, number of rooms to block, optional reason, Active flag, and which channels (including PMS) the block applies to.',
        'Blocks are distribution-aware: you can close OTAs and keep PMS walk-in flexibility — or block both. Always select the channels that must stop selling.',
        'Existing reservations are not magically cancelled. Never block under in-house or arriving guests without a relocation plan coordinated with front office and housekeeping.'
      ),
    ],
    beforeYouBegin: [
      distPerm,
      bullets([
        'Confirm dates with GM / engineering / events',
        'List arrivals already booked on those dates',
        'Know how many rooms of the type must stay offline',
        'Decide whether PMS, OTAs, or both must be blocked',
      ]),
    ],
    steps: [
      heading('Create a block'),
      steps([
        {
          text: 'Open Distribution → Inventory Block and click Add Block.',
        },
        {
          text: 'On Add Event, select Room Type (e.g. Deluxe).',
        },
        {
          text: 'Choose Block Type: Maintenance, Event, Overbooking Protection, Manual Block, Enquiry, or Other.',
        },
        {
          text: 'Set From and To dates (end date must be on/after start).',
        },
        {
          text: 'Set number of rooms to block (respect Max available shown for the range).',
        },
        {
          text: 'Enter Reason (optional but recommended), e.g. “Wing B painting – floors 3–4”.',
        },
        {
          text: 'Toggle Active on. Select channels: include PMS and/or each OTA that must stop sell. Use Select All when closing everywhere.',
        },
        {
          text: 'Save. Verify Channel Inventory / extranet and PMS availability no longer sell those units for the range.',
        },
        {
          text: 'Use Manage Event tab to review, edit, or delete blocks. Release (deactivate/delete) when work completes.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Room Type', 'Yes', 'Category whose inventory is reduced'],
        ['Block Type', 'Yes', 'Maintenance / Event / Overbooking Protection / Manual / Enquiry / Other'],
        ['From / To', 'Yes', 'Inclusive date range for the block'],
        ['Number of rooms', 'Yes', 'How many rooms of the type to remove from sale'],
        ['Reason', 'No', 'Ops note — show on cards for staff clarity'],
        ['Active', 'Yes', 'Whether the block is enforced'],
        ['Channels (+ PMS)', 'Yes', 'Where the stop-sell applies'],
      ]),
      bullets(
        [
          'Add Block button',
          'Block cards (type chip, rooms blocked, inactive badge)',
          'Drawer with Add Event / Manage Event tabs',
        ],
        'What you see'
      ),
    ],
    workflow: [
      workflow('Wing renovation', [
        'List affected bookings',
        'Relocate or rebook guests',
        'Create Maintenance block (PMS + OTAs)',
        'Mark rooms OOO in HK as needed',
        'Complete works',
        'Release block + clear OOO',
        'Re-open allotment',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Blocks reduce sellable inventory for the selected room type and dates on the selected channels. Partial blocks (e.g. 3 of 10 Deluxe) leave remaining rooms sellable.',
        'Overbooking Protection is a deliberate block type for risk control — use when you need a buffer against multi-channel sell-through.',
        'Enquiry blocks can hold stock for tentative business; release or convert when the enquiry confirms or dies.',
        'Room-level OOO/maintenance in Housekeeping and Inventory Blocks complement each other: OOO is often room-number specific; blocks are date/type sales stops across channels.'
      ),
    ],
    tips: [
      callout(
        'warning',
        'Existing reservations',
        'Never block under guests without a relocation plan. Check Booking List for the date range first.'
      ),
      callout(
        'tip',
        'Clear reasons',
        'Write reasons staff understand (“AC plant shutdown”) so night audit and FO know why availability is dark.'
      ),
      callout(
        'important',
        'Select channels',
        'Forgetting to include an OTA leaves that channel selling while PMS is closed — a classic overbook pattern.'
      ),
    ],
    bestPractices: [
      bullets([
        'Calendar-remind yourself to release blocks when work ends.',
        'Align block dates with Housekeeping OOO room status.',
        'Brief FO and reservations when large event blocks go up.',
        'Prefer Active off or delete when done — do not leave stale blocks for next year.',
        'For city-wide events you want to sell, do not create Event blocks by mistake — use Pricing Events instead.',
        'Document who approved Overbooking Protection buffers.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Forgetting to release blocks after renovation completes.',
        'Blocking only PMS while OTAs stay open.',
        'Blocking more rooms than max available for the range.',
        'Using Inventory Block when you meant a Pricing Event (demand spike).',
        'Leaving Enquiry holds Active after the group cancelled.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Still selling on OTA',
            'Channel not selected on block or allotment still open',
            'Edit block channels; set allotment 0 if needed; re-sync; check extranet',
          ],
          [
            'Cannot save dates',
            'End before start or missing room type',
            'Fix From/To; select Room Type',
          ],
          [
            'Max available too low',
            'Existing bookings/blocks in range',
            'Reduce num rooms or free other holds; relocate bookings',
          ],
          [
            'Block shows Inactive',
            'Active toggle off',
            'Edit block and enable Active',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Block vs OOO room?',
          a: 'OOO is often room-level HK/maintenance status. Inventory Blocks are date/type sales stops across PMS/channels. Use both when a physical room is offline and must not sell online.',
        },
        {
          q: 'What is Overbooking Protection?',
          a: 'A block type used to withhold a buffer of rooms from sale so multi-channel sell-through is less likely to overbook.',
        },
        {
          q: 'Can I block only one OTA?',
          a: 'Yes — select that channel and leave others unselected if your strategy requires it. Document why.',
        },
        {
          q: 'Do blocks cancel existing bookings?',
          a: 'No. Relocate or amend bookings separately before locking inventory under guests.',
        },
        {
          q: 'Where else can blocks be managed?',
          a: 'Related inventory tools exist in Manage Inventory workflows; Distribution → Inventory Block is the channel-aware surface described here.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'nexastay-setup',
    title: 'NexaStay Direct Booking Setup',
    moduleId: 'distribution',
    summary:
      'Configure Roomnexa’s direct booking engine: theme, public URL, payments, and preview.',
    keywords: [
      'nexastay',
      'direct booking',
      'booking engine',
      'website booking',
      'razorpay',
      'slug',
    ],
    pathHint: 'Distribution → NexaStay',
    related: ['nexastay-guest', 'manage-rate', 'rate-plans', 'images-media', 'terms-policy'],
    videos: [
      video(
        'XUKo-wi7NBU',
        'Hotel booking engine demo',
        'Increase direct bookings and reduce OTA commissions with NexaStay.'
      ),
    ],
    overview: [
      ...paragraphs(
        'NexaStay is Roomnexa’s direct booking website/engine. Guests book your property without OTA commission when they use your public NexaStay link or embedded website button.',
        'Setup in Distribution controls branding (theme color), the public Hotel URL slug, payment behaviour (pay at hotel, partial deposit via Razorpay, or full payment), and mobile preview so you can QA the guest funnel.',
        'Direct bookings still consume PMS inventory and rates. If Manage Rate is empty, photos are outdated, or rooms are blocked, the public site will show no availability or weak conversion — same truths as OTAs.',
        'Launch only after a test booking lands in the PMS and payment (if enabled) completes or fails gracefully with a clear guest message.'
      ),
    ],
    beforeYouBegin: [
      distPerm,
      bullets([
        'Room types, rates, images, amenities, and terms ready',
        'Payment gateway credentials configured if using Razorpay partial/full pay',
        'Agreed direct-vs-OTA rate strategy (optional direct advantage)',
        'Property WhatsApp/email accurate in Basic Details for guest contact',
      ]),
    ],
    steps: [
      heading('Configure and launch'),
      steps([
        {
          text: 'Open Distribution → NexaStay.',
        },
        {
          text: 'Set theme color (preset or custom hex) and Apply so the public site matches brand.',
        },
        {
          text: 'Set Hotel URL slug (public path segment), save with Set, and copy the full hotel URL chip for website/WhatsApp sharing.',
        },
        {
          text: 'Choose payment mode: Pay at hotel, Partial pay (deposit % via Razorpay), or Full payment (100% via Razorpay) per property policy.',
        },
        {
          text: 'Review other NexaStay settings panels available on the section (content/preview controls as shown).',
        },
        {
          text: 'Use the mobile preview / refresh preview to walk the guest funnel on a phone-sized layout.',
        },
        {
          text: 'Place a test booking for a quiet date; confirm it appears in Booking List and rates/taxes look correct.',
        },
        {
          text: 'Publish the link on your website, Google Business profile, and confirmation templates.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Theme color', 'Recommended', 'Brand accent on the public NexaStay site'],
        ['Hotel URL / slug', 'Yes', 'Public booking address segment'],
        ['Pay at hotel', 'Choice', 'Guest pays on check-in'],
        ['Partial pay', 'Choice', 'Deposit percentage collected via Razorpay'],
        ['Full payment', 'Choice', '100% collected via Razorpay'],
        ['Preview', '—', 'Mobile/desktop preview of the guest site'],
      ]),
      bullets(
        [
          'NexaStay settings panels inside Distribution',
          'Full hotel URL chip + slug field',
          'Payment mode cards',
          'NexaStay mobile preview frame',
        ],
        'What you see'
      ),
    ],
    workflow: [
      workflow('Launch direct', [
        'Finish Configuration masters + rates',
        'Configure NexaStay theme/slug/payments',
        'Test book + pay path',
        'Connect website / WhatsApp button',
        'Monitor conversions vs OTA',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Guest bookings create PMS reservations subject to availability and rates. They appear for FO like any other booking source.',
        'Payment modes determine whether money is collected online (Razorpay partial/full) or at the hotel. Failed online pay should not leave ambiguous inventory — verify the booking status after each test.',
        'Theme and slug changes affect the public site immediately after save; broken slugs 404 guests — keep old marketing links updated.',
        'Coupons, guest login, and public pages (About, Terms, etc.) follow NexaStay guest experience features when enabled — see the NexaStay Guest Experience guide for the public funnel.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Beat OTA rate carefully',
        'A small direct-only advantage mentioned on WhatsApp confirmations can shift share — keep parity rules honest where contracts require.'
      ),
      callout(
        'warning',
        'Payment keys',
        'Broken Razorpay configuration causes checkout failure. Test partial and full pay before promoting the link.'
      ),
      callout(
        'note',
        'Photos sell the stay',
        'Refresh Images after renovations; bait photos drive chargebacks and bad reviews.'
      ),
    ],
    bestPractices: [
      bullets([
        'Mobile-test the entire funnel (search → room → pay → confirmation).',
        'Keep Terms & Policy aligned with what NexaStay shows guests.',
        'Put the NexaStay link in email/WhatsApp signatures for the reservations team.',
        'Monitor first-week bookings for tax display and rate plan inclusions.',
        'Disable online pay modes you cannot operate (e.g. no settlement process for deposits).',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Going live with zero Manage Rate amounts.',
        'Broken payment keys with Full/Partial pay enabled.',
        'Slug collisions or marketing old URLs after rename.',
        'Leaving renovation dates open on NexaStay while OTAs are blocked.',
        'Promising inclusions on the site that rate plans do not include.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No rooms on site',
            'No availability, rates, or types sellable',
            'Check Manage Rate, rooms, Inventory Blocks, room type active flags',
          ],
          [
            'Pay fail',
            'Payment gateway config / Razorpay keys',
            'Fix gateway config; retry test; temporarily use Pay at hotel if needed',
          ],
          [
            'Test booking missing in PMS',
            'Wrong hotel / slug or booking failed mid-pay',
            'Confirm slug property; check Booking List date; review payment status',
          ],
          [
            'Theme not updating',
            'Color not Applied / cache',
            'Click Apply; hard-refresh public preview',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Where is the guest-facing flow documented?',
          a: 'See the NexaStay Guest Experience guide for the public booking journey.',
        },
        {
          q: 'Is NexaStay required to use OTAs?',
          a: 'No. OTAs use Channel Setup/Mapping/Inventory. NexaStay is optional direct acquisition.',
        },
        {
          q: 'Can I use only Pay at hotel?',
          a: 'Yes — choose Pay at hotel when you do not want online collection.',
        },
        {
          q: 'Does Dynamic Pricing affect NexaStay?',
          a: 'Published rates that update Manage Rate / sell rates feed what guests see, subject to your pricing publish process.',
        },
        {
          q: 'Who changes the slug?',
          a: 'Someone with Distribution access — coordinate marketing so printed QR codes and ads stay valid.',
        },
      ]),
    ],
  }),
];

export const pricingGuides = [
  buildGuide({
    id: 'dynamic-pricing-overview',
    title: 'Dynamic Pricing Overview',
    moduleId: 'dynamic-pricing',
    summary:
      'Rules, recommendations, competitors, simulation, forecast, config weights, and publish history for smarter rates.',
    keywords: [
      'dynamic pricing',
      'revenue',
      'recommendations',
      'competitor',
      'forecast',
      'simulate',
    ],
    pathHint: 'Hotel → Dynamic Pricing',
    related: [
      'pricing-recommendations',
      'pricing-rules',
      'pricing-competitors',
      'pricing-simulate',
      'pricing-config-history',
      'manage-rate',
    ],
    overview: [
      ...paragraphs(
        'Dynamic Pricing helps revenue managers improve rates using guardrail rules (min floor, max ceiling, weekend markup, last-minute discount, occupancy surge), competitor context, forecasts, and what-if simulation — then publish carefully into sell rates.',
        'It does not replace Manage Rate as the core tariff surface. Think of Dynamic Pricing as an advisor and controlled publisher sitting on top of your baseline rates and rate plans.',
        'Tabs cover Dashboard, Recommendations, Rules, Events, Competitors (compare / rates / map), Simulate, Forecast, Config, and History (publish logs / recent changes).',
        'Human approval matters: treat suggestions as advisors — especially on sold-out shoulder dates, group contracts, and long-stay negotiated rates that should not swing automatically.'
      ),
      table(
        ['Area', 'Purpose'],
        [
          ['Dashboard', 'Pricing activity overview / publish health'],
          ['Recommendations', 'Suggested rates to review and publish'],
          ['Rules', 'MIN_FLOOR, MAX_CEILING, weekend, last-minute, occupancy surge'],
          ['Events', 'Holidays and special demand dates'],
          ['Competitors', 'Compare, rates, map / profiles'],
          ['Simulate', 'What-if — no live publish'],
          ['Forecast', 'Demand / rate outlook'],
          ['Config', 'Signal weights, floors, signal toggles'],
          ['History', 'Publish logs and recent changes'],
        ]
      ),
    ],
    beforeYouBegin: [
      pricePerm,
      bullets([
        'Reliable Manage Rate baseline for room types / rate plans',
        'Agreed floor rates with ownership (never below true cost reality)',
        'Understand which rate plans may be published vs contract-only',
        'Package includes Dynamic Pricing for this hotel',
      ]),
    ],
    steps: [
      heading('Weekly rhythm'),
      steps([
        {
          text: 'Open Dynamic Pricing → Config. Set formula weights (occupancy, lead time, velocity, competitor) and guardrail multipliers; enable only signals you trust.',
        },
        {
          text: 'Open Rules. Create MIN_FLOOR / MAX_CEILING and optional WEEKEND_MARKUP, LAST_MINUTE_DISCOUNT, OCCUPANCY_SURGE rules with clear names.',
        },
        {
          text: 'Add Events for holidays, conferences, and local demand spikes.',
        },
        {
          text: 'Refresh Competitors (compare / rates / map) so suggestions are not blind.',
        },
        {
          text: 'Review Recommendations; run Simulate for risky weeks.',
        },
        {
          text: 'Publish approved changes; verify Manage Rate / channels; monitor History publish logs.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'NotchTabs / routes: dashboard, recommendations, rules, events, competitor, simulate, forecast, config, history',
          'Competitor sub-tabs: compare, rates, map',
          'History sub-tabs: publish-logs, recent',
        ],
        'Pricing workspace'
      ),
    ],
    workflow: [
      workflow('Weekly revenue meeting', [
        'Forecast',
        'Competitors',
        'Recommendations',
        'Simulate risky dates',
        'Publish',
        'Monitor pickup 48h',
        'Audit History',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Rules constrain suggestions (floors, ceilings, markups/discounts). Config weights blend live signals; turning a signal off excludes it entirely — stronger than setting weight to 0.',
        'Publishing applies approved rates into the sell-rate path used by bookings and channels according to your integration. Simulation never sells rooms until you publish.',
        'Events and competitor data are inputs to context — stale comps or missing events produce odd spikes. Always sanity-check before publish.',
        'Corporate/group contracts negotiated offline should not be blindly overwritten by dynamic suggestions; keep those plans controlled.'
      ),
    ],
    tips: [
      callout(
        'warning',
        'Human approval',
        'Treat AI/engine suggestions as advisors — especially on near-full dates and contracted segments.'
      ),
      callout(
        'tip',
        'Simulate first',
        'Use Simulate before festival weekends. Publish a subset, then expand if pickup looks healthy.'
      ),
      callout(
        'note',
        'FO communication',
        'Tell front office when large rate swings publish so quotes on the phone stay consistent.'
      ),
    ],
    bestPractices: [
      bullets([
        'Never set floor below true cost and tax reality.',
        'Log why you overrode a recommendation (History + internal notes).',
        'Review rules and weights quarterly — not daily thrashing.',
        'Keep competitor set comparable (star, location, product).',
        'Batch publish by week where possible.',
        'Keep a rollback plan via Manage Rate if a publish misfires.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Publishing huge swings without FO/sales notice.',
        'Ignoring competitor map quality / wrong comps.',
        'Floor higher than ceiling rules.',
        'Blind Accept all on Recommendations.',
        'Tweaking Config weights daily without measuring pickup.',
        'Using Pricing Events and Inventory Blocks interchangeably.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No recommendations',
            'Config/rules/data history insufficient',
            'Check Config signals, rules, baseline Manage Rate, booking history',
          ],
          [
            'Module missing',
            'Package or manage_dynamic_pricing',
            'Ask admin to enable Dynamic Pricing for role/hotel',
          ],
          [
            'Publish failed',
            'Validation or downstream rate write error',
            'Check History publish logs; fix rates; retry subset',
          ],
          [
            'Odd spikes',
            'Events or competitor inputs',
            'Review Events + Competitors; tighten MAX_CEILING',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Does this replace Manage Rate?',
          a: 'No — it informs and can update pricing; Manage Rate remains a core tariff surface for baselines and manual control.',
        },
        {
          q: 'Who should publish?',
          a: 'Revenue managers or GMs with manage_dynamic_pricing — not every FO agent.',
        },
        {
          q: 'Will OTAs update automatically?',
          a: 'Published sell rates flow according to your rate/distribution integrations. Still verify critical dates on Channel Inventory / extranet.',
        },
        {
          q: 'What about corporate rates?',
          a: 'Keep contracted plans separate and enforce contracted amounts in Manage Rate; do not let surge rules overwrite contracts.',
        },
        {
          q: 'Where do I see who published?',
          a: 'History → Publish logs / recent changes for auditability.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pricing-recommendations',
    title: 'Pricing Recommendations',
    moduleId: 'dynamic-pricing',
    summary: 'Review suggested rates against current tariffs and decide what to publish.',
    keywords: ['pricing recommendations', 'suggested rates', 'publish rates'],
    pathHint: 'Dynamic Pricing → Recommendations',
    related: ['pricing-rules', 'pricing-simulate', 'manage-rate', 'pricing-config-history'],
    overview: [
      ...paragraphs(
        'Recommendations propose rates from your rules, config signal weights, demand signals, events, and competitor context.',
        'The job of the revenue user is not blind acceptance — it is to compare suggested vs current, exclude outliers, simulate when unsure, then publish approved rows.',
        'Near-full dates often suggest increases; empty shoulder dates may suggest discounts subject to MIN_FLOOR. Always check remaining inventory strategy and contracted business.',
        'After publish, confirm Manage Rate / selling channels and watch pickup. Use History if you need to audit what went out.'
      ),
    ],
    beforeYouBegin: [
      pricePerm,
      bullets([
        'Rules and Config saved',
        'Events added for known demand spikes',
        'Competitor set reasonably current',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Dynamic Pricing → Recommendations.',
        },
        {
          text: 'Filter or focus on the dates and room types you are pricing this session.',
        },
        {
          text: 'Compare suggested rate vs current rate; note large % swings.',
        },
        {
          text: 'Exclude outliers (contract dates, closed renovation days, obvious data glitches).',
        },
        {
          text: 'Optional: jump to Simulate for a what-if on the same week.',
        },
        {
          text: 'Publish only approved changes. Verify on Manage Rate and spot-check an OTA/NexaStay date.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Suggestion list/grid', '—', 'Proposed rates by date/type/plan as provided'],
        ['Compare vs current', '—', 'Decision aid before publish'],
        ['Publish actions', '—', 'Apply approved recommendations to sell rates'],
        ['Filters (dates/types)', 'Recommended', 'Scope the review to a manageable set'],
      ]),
    ],
    workflow: [
      workflow('Approve suggestions', [
        'Refresh recommendations',
        'Review vs current',
        'Exclude outliers',
        'Simulate if needed',
        'Publish',
        'Verify Manage Rate / channels',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Suggestions respect floors/ceilings and rule markups when configured. If rules are too tight, recommendations may barely move; if too loose, swings can be extreme.',
        'Publishing writes rates into the live path — it is not a draft after success. Use Simulate beforehand when the week is strategic.',
        'Recommendations are only as good as inputs: missing events, bad comps, or empty baselines produce noise.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Sold-out dates',
        'Raising rates on near-full dates is common; ensure the remaining inventory strategy matches (protect walk-in vs squeeze ADR).'
      ),
      callout(
        'warning',
        'Blind accept',
        'Never Accept all on a festival week without scanning each spike against Events and comps.'
      ),
      callout(
        'note',
        'Batch by week',
        'Pricing one week at a time keeps FO quotes coherent and audits simpler.'
      ),
    ],
    bestPractices: [
      bullets([
        'Batch publish by week.',
        'Keep a short note of overrides for the revenue meeting.',
        'Recheck recommendations after major Config/Rules changes.',
        'Align publish timing with channel update mode expectations.',
        'Exclude dates under Inventory Block / renovation.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Blind accept all.',
        'Publishing on dates already fully contracted at fixed rates.',
        'Ignoring a suggestion that drops below cost after tax.',
        'Publishing then immediately changing Config without measuring results.',
        'Forgetting to tell FO about a sharp weekend increase.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Odd spikes',
            'Events/competitor inputs',
            'Review Events + Competitors; adjust MAX_CEILING',
          ],
          [
            'No movement',
            'Rules too tight or signals off',
            'Relax floors/ceilings carefully; enable needed signals in Config',
          ],
          [
            'Publish button fails',
            'Permission or downstream error',
            'Confirm manage_dynamic_pricing; check History logs',
          ],
          [
            'Empty list',
            'Insufficient data or filters',
            'Widen date range; ensure baseline rates exist',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can FO publish?',
          a: 'Usually revenue/manager roles only. FO should escalate pricing exceptions.',
        },
        {
          q: 'Do recommendations auto-apply?',
          a: 'No — you review and publish. Simulation also does not auto-apply.',
        },
        {
          q: 'What if I disagree with a suggestion?',
          a: 'Skip it. Tighten rules/config if the engine keeps proposing unacceptable values.',
        },
        {
          q: 'Will history show my publish?',
          a: 'Yes — use History → Publish logs / recent changes.',
        },
        {
          q: 'Should I publish mid-day?',
          a: 'Prefer planned windows. Mid-day publishes are fine for tactical moves if FO is informed.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pricing-rules',
    title: 'Pricing Rules',
    moduleId: 'dynamic-pricing',
    summary:
      'Configure min floor, max ceiling, weekend markup, last-minute discount, and occupancy surge rules.',
    keywords: [
      'pricing rules',
      'weekend markup',
      'last minute',
      'occupancy surge',
      'min floor',
      'max ceiling',
    ],
    pathHint: 'Dynamic Pricing → Rules',
    related: ['dynamic-pricing-overview', 'pricing-events', 'pricing-config-history'],
    overview: [
      ...paragraphs(
        'Rules encode your revenue policy so recommendations stay inside guardrails. Each rule has a name, rule type, numeric value, and Active flag.',
        'Supported rule types: Minimum floor (MIN_FLOOR), Maximum ceiling (MAX_CEILING), Weekend markup % (WEEKEND_MARKUP), Last-minute discount % (LAST_MINUTE_DISCOUNT), Occupancy surge % (OCCUPANCY_SURGE).',
        'Floors protect contribution margin; ceilings prevent embarrassing public rates after a bad signal day. Weekend and surge rules capture predictable demand shapes without manual edits every Friday.',
        'Rules constrain automated suggestions — they do not replace judgement for groups, corporate contracts, or one-off VIP rates negotiated offline.'
      ),
    ],
    beforeYouBegin: [
      pricePerm,
      bullets([
        'Agree floors/ceilings with ownership using real cost + tax',
        'Decide weekend markup and last-minute discount policy',
        'Know occupancy thresholds that should trigger surge behaviour',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Dynamic Pricing → Rules.',
        },
        {
          text: 'Click Add. Enter a clear Name (e.g. “Deluxe MIN_FLOOR 3500”).',
        },
        {
          text: 'Select Rule Type: Minimum floor, Maximum ceiling, Weekend markup %, Last-minute discount %, or Occupancy surge %.',
        },
        {
          text: 'Enter Value (currency amount for floors/ceilings, or percent for markup/discount/surge — per type meaning).',
        },
        {
          text: 'Set Active on. Save. Repeat for complementary rules (floor + ceiling together).',
        },
        {
          text: 'Re-open Recommendations / Simulate on a sample week to confirm behaviour.',
        },
        {
          text: 'Edit or delete rules from the list when policy changes; prefer deactivating before deleting if you may reuse.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Name', 'Yes', 'Staff label for the rule'],
        ['Rule Type', 'Yes', 'MIN_FLOOR / MAX_CEILING / WEEKEND_MARKUP / LAST_MINUTE_DISCOUNT / OCCUPANCY_SURGE'],
        ['Value', 'Yes', 'Amount or percent depending on type'],
        ['Active', 'Yes', 'Whether the rule constrains suggestions'],
        ['Add / Edit / Delete', '—', 'Lifecycle controls on the Rules tab'],
      ]),
    ],
    workflow: [
      workflow('Set guardrails', [
        'Agree floors with owner',
        'Enter MIN_FLOOR + MAX_CEILING',
        'Add weekend / last-minute / surge as needed',
        'Test on sample week',
        'Roll out publish carefully',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'MIN_FLOOR and MAX_CEILING bound recommended rates. If floor > ceiling due to misconfiguration, recommendations may stall or behave unexpectedly — always keep floor ≤ ceiling in policy terms.',
        'WEEKEND_MARKUP raises weekend pricing pressure by a percentage. LAST_MINUTE_DISCOUNT can stimulate late pickup within floor constraints. OCCUPANCY_SURGE increases rates as occupancy rises.',
        'Inactive rules do not apply. Multiple active rules combine with Config weights and Events — review holistically after changes.',
        'Negotiated corporate deals should live on dedicated rate plans with manual Manage Rate control rather than relying on dynamic surge alone.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Corporate rates',
        'Negotiate corporate deals outside pure dynamic rules when contracts exist; protect those plans from surge publish.'
      ),
      callout(
        'tip',
        'Name with intent',
        'Include room class and number in the rule name so audits are readable six months later.'
      ),
      callout(
        'warning',
        'Aggressive surge',
        'Over-aggressive OCCUPANCY_SURGE can price you out and leave empty rooms — simulate first.'
      ),
    ],
    bestPractices: [
      bullets([
        'Review rules quarterly.',
        'Document rule intent in your revenue SOP.',
        'Pair every MIN_FLOOR with a sensible MAX_CEILING.',
        'Retest recommendations after activating surge rules.',
        'Deactivate seasonal rules when the season ends.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Floor higher than ceiling.',
        'Surge too aggressive causing empty rooms.',
        'Last-minute discount fighting weekend markup without review.',
        'Leaving test rules Active on the live hotel.',
        'Deleting rules instead of deactivating during experiments.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No movement in rates',
            'Rules too tight or data insufficient',
            'Widen ceiling; check Config signals; ensure baseline rates',
          ],
          [
            'Rates too high',
            'Surge/weekend markup stacked',
            'Lower percentages; verify Events; use Simulate',
          ],
          [
            'Rates below cost',
            'Floor missing/inactive or too low',
            'Set Active MIN_FLOOR to true cost-aware value',
          ],
          [
            'Cannot save rule',
            'Missing name/value',
            'Complete required fields',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'How do I do seasonal pricing?',
          a: 'Combine Events + rules + Manage Rate seasonal baselines. Raise floors before festival periods.',
        },
        {
          q: 'Is value always a percent?',
          a: 'No. Floors/ceilings are amount-style bounds; weekend, last-minute, and surge types are percentage behaviours as labelled in the UI.',
        },
        {
          q: 'Can rules publish by themselves?',
          a: 'Rules shape recommendations; you still publish from Recommendations (unless your process says otherwise).',
        },
        {
          q: 'Do rules affect past bookings?',
          a: 'No. They influence future suggestions/publishes, not historical folio rates.',
        },
        {
          q: 'What if I need a one-week exception?',
          a: 'Use Events + careful publish, or temporarily adjust Manage Rate for those dates without rewriting global rules.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pricing-events',
    title: 'Pricing Events',
    moduleId: 'dynamic-pricing',
    summary: 'Mark holidays and special events that should influence rates.',
    keywords: ['pricing events', 'holidays', 'special events', 'festival', 'conference'],
    pathHint: 'Dynamic Pricing → Events',
    related: ['pricing-rules', 'pricing-simulate', 'inventory-blocks'],
    overview: [
      ...paragraphs(
        'Pricing Events flag dates like festivals, conferences, city marathons, or local demand spikes so recommendation and forecast context can respond.',
        'Unlike Inventory Blocks (which stop sales), Events tell the pricing engine that demand should be stronger — usually implying higher willingness to raise rates within your floors/ceilings.',
        'Add events weeks ahead when dates are known. Include shoulder nights if demand spills before/after the main day.',
        'Remove or deactivate one-off events after they pass so next year’s recommendations are not haunted by stale spikes.'
      ),
    ],
    beforeYouBegin: [
      pricePerm,
      bullets([
        'City/hotel event calendar from sales & GM',
        'Know which dates need higher floors vs full stop-sell (block instead)',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Dynamic Pricing → Events.',
        },
        {
          text: 'Add event with a clear name (e.g. “City Marathon Weekend”).',
        },
        {
          text: 'Set start and end dates covering demand shoulders if needed.',
        },
        {
          text: 'Save. Raise floors or review Rules if this event needs stronger guardrails.',
        },
        {
          text: 'Refresh Recommendations / Simulate around those dates before publishing.',
        },
        {
          text: 'After the event, delete or clear one-offs you will not reuse next year.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Event name', 'Yes', 'Label for staff and engine context'],
        ['Start / end dates', 'Yes', 'Demand window influencing pricing'],
        ['Add / Edit / Delete', '—', 'Maintain the event calendar'],
      ]),
    ],
    workflow: [
      workflow('Festival prep', [
        'Add Pricing Event',
        'Review Rules floors',
        'Simulate week',
        'Publish rates',
        'Watch pickup',
        'Clear stale event later',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Events are inputs to recommendation/forecast context. They do not by themselves close inventory — use Inventory Block when you must stop sell.',
        'Multi-day events should use inclusive ranges. If Saturday is sold out from demand, consider Friday/Sunday shoulders in the same event or separate events.',
        'Stacking Events with aggressive OCCUPANCY_SURGE can overshoot — Simulate before publish.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Lead time',
        'Add city marathon/wedding-season dates weeks ahead so recommendations ramp before OTAs fill.'
      ),
      callout(
        'warning',
        'Event ≠ Block',
        'Do not create an Inventory Block when you intend to sell through a festival at higher ADR — use Pricing Events.'
      ),
      callout(
        'note',
        'Share the calendar',
        'Send the event list to FO and sales so phone quotes match published rates.'
      ),
    ],
    bestPractices: [
      bullets([
        'Share event calendar with FO/sales.',
        'Name events with year (“Diwali 2026”) for clarity.',
        'Review upcoming 90 days every Monday.',
        'Pair big events with competitor rate checks.',
        'Clean up past one-offs quarterly.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Forgetting to remove one-off events next year.',
        'Using Inventory Block instead of Pricing Event (or vice versa).',
        'Omitting shoulder nights that still spike.',
        'Adding events the morning of the festival (too late for lead-time pickup).',
        'Duplicate overlapping events causing confusion.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No effect on suggestions',
            'Recommendations not refreshed / rules dominate',
            'Refresh recommendations; check floors/ceilings; Simulate',
          ],
          [
            'Rates too high on event',
            'Event + surge stacking',
            'Tighten MAX_CEILING; reduce surge; re-simulate',
          ],
          [
            'Wrong dates',
            'Data entry error',
            'Edit event dates; republish carefully',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Multi-day events?',
          a: 'Use inclusive date ranges covering shoulder nights if demand spills over.',
        },
        {
          q: 'Can events close rooms?',
          a: 'No — use Inventory Block / allotment 0 to stop sell.',
        },
        {
          q: 'Who maintains the calendar?',
          a: 'Revenue or GM — with sales feeding conference/wedding leads.',
        },
        {
          q: 'Do guests see event names?',
          a: 'Events are pricing inputs for staff tools; guest-facing text lives in marketing/NexaStay/OTA content.',
        },
        {
          q: 'Recurring annual festivals?',
          a: 'Create fresh dated events each year (or update dates) so the engine uses the correct window.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pricing-competitors',
    title: 'Competitor Rates & Map',
    moduleId: 'dynamic-pricing',
    summary:
      'Compare competitor rates, manage competitor profiles, and use map views for your comp set.',
    keywords: [
      'competitors',
      'comp set',
      'rate shop',
      'map',
      'compare',
      'competitor sync',
    ],
    pathHint: 'Dynamic Pricing → Competitors',
    related: ['pricing-recommendations', 'pricing-simulate', 'dynamic-pricing-overview'],
    overview: [
      ...paragraphs(
        'Competitor tools help you see how nearby hotels price — so recommendations are grounded in market reality, not only internal occupancy.',
        'The Competitors area includes Compare, Rates, and Map / profile discovery sub-views. Build a true comp set: similar star, location, and product — not luxury vs budget.',
        'Where enabled, competitor discovery/sync assists updates; you should still verify before letting competitor weight dominate Config.',
        'Stale or wrong comps mislead suggestions. Refresh when a new hotel opens nearby or a remodel changes a competitor’s market position.'
      ),
    ],
    beforeYouBegin: [
      pricePerm,
      bullets([
        'List 4–8 true competitors (not aspirational brands)',
        'Competitor signal enabled in Config only when data quality is acceptable',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Dynamic Pricing → Competitors.',
        },
        {
          text: 'Use Compare to shop rates for target stay dates against your set.',
        },
        {
          text: 'Use Rates views to inspect competitor rate details available in the product.',
        },
        {
          text: 'Use Map / profiles to discover or verify competitor locations and profiles; add/confirm your set.',
        },
        {
          text: 'Run competitor sync where available when rows need refresh; watch sync progress dialogs to completion.',
        },
        {
          text: 'Return to Recommendations with market context; adjust strategy then publish.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Compare', '—', 'Side-by-side rate shopping for dates'],
        ['Rates', '—', 'Competitor rate details'],
        ['Map / profiles', '—', 'Geographic discovery and competitor profiles'],
        ['Sync actions', 'When enabled', 'Refresh competitor rate data'],
      ]),
    ],
    workflow: [
      workflow('Comp shop', [
        'Pick dates',
        'Compare comps',
        'Adjust strategy / rules',
        'Simulate',
        'Publish',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Competitor data quality affects suggestions when competitor_weight is active in Config. If you have no synced data, disable the Competitor signal rather than trusting zeros.',
        'Matching on price alone ignoring product differences (suite vs standard, breakfast included vs RO) causes bad decisions — compare like with like.',
        'Sync operations may take time; wait for completion indicators before judging recommendations.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'True comps',
        'Compare similar star/location/product — not luxury vs budget.'
      ),
      callout(
        'note',
        'Signal off until ready',
        'In Config, disable Competitor signal until sync quality is trustworthy.'
      ),
      callout(
        'warning',
        'Race to the bottom',
        'Do not auto-match the cheapest comp every day — protect brand and floors.'
      ),
    ],
    bestPractices: [
      bullets([
        'Refresh comps when new hotels open nearby.',
        'Keep the set small and honest (quality over quantity).',
        'Shop the same lead-time window guests actually book.',
        'Document why a hotel is in or out of the set.',
        'Revisit comps after major renovations (yours or theirs).',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Matching on price alone ignoring product differences.',
        'Including aspirational 5-stars that never compete for your guests.',
        'Leaving competitor signal on with empty/stale data.',
        'Changing the whole set every week (no trend learning).',
        'Publishing purely because one competitor undercut for a night.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Missing comps',
            'Profiles not added / Places connectivity',
            'Add profiles via map/discovery; retry sync; check connectivity',
          ],
          [
            'Sync stuck',
            'Network or provider delay',
            'Wait for progress dialog; retry; reduce set if needed',
          ],
          [
            'Suggestions chase bad comps',
            'Wrong set or high competitor weight',
            'Fix set; lower competitor_weight or disable signal',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'AI / competitor rate sync?',
          a: 'Where enabled, competitor rate sync assists updates — still verify before publish.',
        },
        {
          q: 'How many competitors?',
          a: 'Typically a focused set of comparable hotels beats a long noisy list.',
        },
        {
          q: 'Does map distance matter?',
          a: 'Yes — guests compare nearby alternatives. Distant hotels rarely belong in the set.',
        },
        {
          q: 'Can FO edit competitors?',
          a: 'Usually revenue only. Inconsistent comps create chaotic recommendations.',
        },
        {
          q: 'What if a competitor closes?',
          a: 'Remove them from the set promptly so recommendations stop referencing dead supply.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pricing-simulate',
    title: 'Pricing Simulate & Forecast',
    moduleId: 'dynamic-pricing',
    summary: 'What-if simulation and demand/rate forecast before you publish.',
    keywords: ['simulate', 'forecast', 'what if', 'demand outlook'],
    pathHint: 'Dynamic Pricing → Simulate / Forecast',
    related: ['pricing-recommendations', 'dynamic-pricing-overview', 'pricing-events'],
    overview: [
      ...paragraphs(
        'Simulate runs safe what-if scenarios over your dates — no rates are published to PMS or channels until you explicitly publish from Recommendations (or your publish flow).',
        'Forecast projects demand/rate outlook to plan staffing, purchasing, and pricing posture. It is predictive, not a guarantee of occupancy.',
        'Use Simulate before festival spikes, after large Config/Rules changes, and whenever Recommendations propose double-digit swings.',
        'Judge live results after enough booking lead time — pickup lag means next-hour ADR is rarely the right success metric.'
      ),
    ],
    beforeYouBegin: [
      pricePerm,
      bullets([
        'Known scenario (e.g. +10% weekend, new floor, event week)',
        'Historical booking volume available for meaningful forecast',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Dynamic Pricing → Simulate.',
        },
        {
          text: 'Define the scenario inputs available on the screen (dates / rate ideas as provided).',
        },
        {
          text: 'Run simulation and review projected impact charts/results. Confirm the UI still shows safe preview — not published.',
        },
        {
          text: 'Open Forecast for demand context on the same horizon.',
        },
        {
          text: 'If outcomes look acceptable, publish a subset via Recommendations — not the entire season at once.',
        },
        {
          text: 'Monitor pickup for 48 hours; keep a Manage Rate rollback plan.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Scenario inputs', 'Yes', 'What-if parameters for the simulation'],
        ['Result charts', '—', 'Projected impact visualization'],
        ['Safe preview banner', '—', 'Confirms no rates published from Simulate'],
        ['Forecast views', '—', 'Demand / rate outlook'],
      ]),
    ],
    workflow: [
      workflow('Cautious change', [
        'Simulate +10% (example)',
        'Review forecast',
        'Publish subset',
        'Monitor 48h',
        'Expand or rollback',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Simulation does not sell rooms and does not write live tariffs. Only publish actions change what guests pay.',
        'Forecast quality depends on historical booking volume and current pace. New hotels see thinner forecasts until history accumulates.',
        'Use Simulate after Config weight changes — weights alter recommendations; simulation helps you see direction before committing.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Pickup lag',
        'Judge results after enough booking lead time — not within an hour of publish.'
      ),
      callout(
        'tip',
        'Festival spikes',
        'Simulate before festival weekends even if you “already know” you will raise rates.'
      ),
      callout(
        'warning',
        'Owner reporting',
        'Do not present simulation outputs as booked revenue. Label them as scenarios.'
      ),
    ],
    bestPractices: [
      bullets([
        'Simulate before festival spikes.',
        'Keep a rollback plan in Manage Rate.',
        'Publish subsets, then expand.',
        'Pair forecast with staffing notes for FO/HK.',
        'Re-run Simulate after major rule changes.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Publishing mid-simulation assumptions as fact to owners.',
        'Skipping Simulate on double-digit recommendation swings.',
        'Expecting forecast to be exact occupancy.',
        'Simulating but forgetting to publish the approved plan.',
        'No monitoring after publish.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Empty forecast',
            'Need historical booking volume',
            'Wait for history; ensure bookings are in PMS; widen horizon',
          ],
          [
            'Simulation empty/errors',
            'Missing inputs or baseline rates',
            'Complete scenario fields; ensure Manage Rate baselines',
          ],
          [
            'Thought Simulate published',
            'Misread UI',
            'Confirm safe preview messaging; check History for actual publishes',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'History logs?',
          a: 'Use History → publish logs / recent changes to audit who published what — separate from Simulate.',
        },
        {
          q: 'Does Simulate update OTAs?',
          a: 'No. Only successful publish flows update sell rates/channels.',
        },
        {
          q: 'Forecast vs Recommendations?',
          a: 'Forecast is outlook; Recommendations are concrete rate suggestions to approve.',
        },
        {
          q: 'Can I simulate competitor undercut?',
          a: 'Use scenario tools available on Simulate plus Competitors context; still apply judgement and floors.',
        },
        {
          q: 'How often should I forecast?',
          a: 'At least weekly in peak; more often around major Events.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'pricing-config-history',
    title: 'Pricing Config & History',
    moduleId: 'dynamic-pricing',
    summary:
      'Algorithm configuration (weights, floors, signals) and publish history logs.',
    keywords: [
      'pricing config',
      'publish logs',
      'pricing history',
      'weights',
      'occupancy weight',
    ],
    pathHint: 'Dynamic Pricing → Config / History',
    related: ['pricing-rules', 'pricing-recommendations', 'pricing-simulate'],
    overview: [
      ...paragraphs(
        'Config tunes how aggressive the pricing engine is: formula weights for occupancy, lead time, velocity, and competitor signals; base-rate multipliers for floor/ceiling behaviour; and signal toggles that include or exclude inputs entirely.',
        'Weights are 0–100 and do not need to sum to 100 — the system scales active signals. Turning a signal off excludes it completely (stronger than weight 0).',
        'History provides accountability: Publish logs (batches) and Recent changes so you can audit who published what and investigate rate jumps.',
        'Only revenue leads should edit Config. Tweaking weights daily without measurement creates noise and erodes trust in recommendations.'
      ),
    ],
    beforeYouBegin: [
      pricePerm,
      bullets([
        'Revenue lead agreed on starting weights',
        'Know which signals have trustworthy data (especially Competitor)',
      ]),
    ],
    steps: [
      heading('Config'),
      steps([
        {
          text: 'Open Dynamic Pricing → Config.',
        },
        {
          text: 'Adjust Formula weights: Occupancy, Lead time, Velocity, Competitor (sliders with help tips).',
        },
        {
          text: 'Set base rate × multiplier style floor/ceiling guidance fields as shown (e.g. helper text like 0.5 → floor ≈ half of base).',
        },
        {
          text: 'Under Signals, enable only feeds you trust; disable Competitor until sync quality is good.',
        },
        {
          text: 'Save. Use Simulate to preview impact before publishing rates.',
        },
      ]),
      heading('History'),
      steps([
        {
          text: 'Open Dynamic Pricing → History.',
        },
        {
          text: 'Review Publish logs for batch success/failure counts.',
        },
        {
          text: 'Open Recent changes for a finer audit of rate updates.',
        },
        {
          text: 'If a bad publish occurred, correct via Manage Rate / controlled republish and document the incident.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Occupancy weight', 'Config', 'How strongly occupancy influences suggestions'],
        ['Lead time weight', 'Config', 'Influence of booking lead time'],
        ['Velocity weight', 'Config', 'Influence of booking pace/velocity'],
        ['Competitor weight', 'Config', 'Influence of competitor rates'],
        ['Signal toggles', 'Config', 'Include/exclude each data feed entirely'],
        ['Floor / ceiling multipliers', 'Config', 'Guardrails relative to base rate'],
        ['Publish logs', 'History', 'Batch publish audit trail'],
        ['Recent changes', 'History', 'Recent rate change list'],
      ]),
    ],
    workflow: [
      workflow('Audit a rate jump', [
        'Open History',
        'Find publish batch',
        'Confirm intent / user',
        'Rollback via Manage Rate if needed',
        'Tighten Config/Rules',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Config changes alter future recommendations and automation behaviour after save. They do not rewrite past folios.',
        'Signal off means omitted from the engine — not merely down-weighted. Pair toggles with weights deliberately.',
        'History is append-style audit data for publishes and recent changes. No history rows usually means nothing has been published yet.',
        'Dashboard may show publish success rates and activity — use it together with History when diagnosing failed publishes.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Least privilege',
        'Only revenue managers should edit Config.'
      ),
      callout(
        'note',
        'Simulate after Config',
        'Always Simulate after material weight changes before a wide publish.'
      ),
      callout(
        'warning',
        'Competitor cold start',
        'Disable Competitor signal until you have synced trustworthy comps.'
      ),
    ],
    bestPractices: [
      bullets([
        'Screenshot config after material changes.',
        'Review History weekly.',
        'Change one weight group at a time and measure pickup.',
        'Document default weight baselines for the property.',
        'Investigate failed publish counts the same day.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Tweaking weights daily without measurement.',
        'Leaving Competitor on with empty data.',
        'Assuming Config publish — Config save is not rate publish.',
        'Ignoring failed rows in publish logs.',
        'Giving all managers Config edit rights.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'No history rows',
            'No publishes yet',
            'Publish from Recommendations after review',
          ],
          [
            'Recommendations wild after Config save',
            'Weights/signals mis-set',
            'Revert toward defaults; disable bad signals; Simulate',
          ],
          [
            'Publish success rate low',
            'Downstream validation failures',
            'Open publish logs; fix offending dates/types; retry',
          ],
          [
            'Cannot save Config',
            'Permission or validation',
            'Confirm manage_dynamic_pricing; check required numeric fields',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Who changed rates?',
          a: 'Check publish logs / recent history and your user audit practices.',
        },
        {
          q: 'Do weights need to total 100?',
          a: 'No — values are 0–100 and are scaled among active signals.',
        },
        {
          q: 'Config vs Rules?',
          a: 'Config blends signals and global multipliers; Rules are explicit floor/ceiling/markup policies.',
        },
        {
          q: 'Does saving Config publish rates?',
          a: 'No. Save updates engine settings; publish is a separate Recommendations action.',
        },
        {
          q: 'Where is automation discussed in-product?',
          a: 'Config help text covers how settings affect recommendations and advises Simulate before publishing.',
        },
      ]),
    ],
  }),
];
